
"use client";

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  Timestamp,
} from "firebase/firestore";

import { fireDB } from "../../../Backend/Firebase/FirebaseConfigData";

import { toast, ToastContainer } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";

import "react-toastify/dist/ReactToastify.css";

import BlogEditor from "../createChapter/BlogEditor";

// ----------------------------------------------------
// Interfaces
// ----------------------------------------------------

interface SectionData {
  sectionTitle: string;
  content: string;
}

interface ChapterData {
  chapterSlug: string;
  chapterSlugManual?: boolean;
  contentTitle: string;
  title: string;
  titleManual?: boolean;
  content: string;
  author: string;
  seoTitle: string;
  seoDescription: string;
  seoTags: string;
  seoHeading: string;
  seoHeadingManual?: boolean;
  canonicalUrl: string;
  createdAt: string;
  sections?: SectionData[];
}

interface EditorSection extends SectionData {
  localId: string;
}

type ChapterFormData = Omit<ChapterData, "sections"> & {
  sections: EditorSection[];
};

// ----------------------------------------------------
// Helpers
// ----------------------------------------------------

const createLocalId = () =>
  `section-${crypto.randomUUID()}`;

const initialChapterData = (): ChapterFormData => ({
  chapterSlug: "",
  chapterSlugManual: false,
  contentTitle: "",
  title: "",
  titleManual: false,
  content: "",
  author: "",
  seoTitle: "",
  seoDescription: "",
  seoTags: "",
  seoHeading: "",
  seoHeadingManual: false,
  canonicalUrl: "",
  createdAt: new Date().toISOString(),
  sections: [],
});

// ----------------------------------------------------
// Component
// ----------------------------------------------------

export default function UpdateChapterPage() {
  const { librarySlug, bookSlug, chapterSlug } = useParams<{
    librarySlug: string;
    bookSlug: string;
    chapterSlug: string;
  }>();

  const [chapterData, setChapterData] =
    useState<ChapterFormData>(initialChapterData);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadedChapterSlug, setLoadedChapterSlug] = useState("");

  // ----------------------------------------------------
  // Load chapter
  // ----------------------------------------------------

  useEffect(() => {
    let cancelled = false;

    const fetchChapter = async () => {
      setLoading(true);

      if (!librarySlug || !bookSlug || !chapterSlug) {
        toast.error("Missing chapter identifiers");
        setLoading(false);
        return;
      }

      try {
        const chapterRef = doc(
          fireDB,
          "NewBlogs",
          librarySlug,
          "books",
          bookSlug,
          "chapters",
          chapterSlug
        );

        const snapshot = await getDoc(chapterRef);

        if (cancelled) return;

        if (!snapshot.exists()) {
          toast.error("Chapter not found");
          return;
        }

        const data = snapshot.data() as Partial<ChapterData>;

        const formattedSections: EditorSection[] = (
          Array.isArray(data.sections) ? data.sections : []
        ).map((section) => ({
          localId: createLocalId(),
          sectionTitle: section.sectionTitle || "",
          content: section.content || "",
        }));

        setChapterData({
          ...initialChapterData(),
          ...data,
          chapterSlug: data.chapterSlug || chapterSlug,
          sections: formattedSections,
        });

        setLoadedChapterSlug(chapterSlug);
      } catch (error) {
        if (!cancelled) {
          console.error("Error fetching chapter:", error);
          toast.error("Error fetching chapter data");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchChapter();

    return () => {
      cancelled = true;
    };
  }, [librarySlug, bookSlug, chapterSlug]);

  // ----------------------------------------------------
  // Update text fields
  // ----------------------------------------------------

  const updateField = (
    field: keyof ChapterFormData,
    value: string
  ) => {
    setChapterData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // ----------------------------------------------------
  // Add section
  // ----------------------------------------------------

  const addSection = () => {
    setChapterData((prev) => ({
      ...prev,
      sections: [
        ...prev.sections,
        {
          localId: createLocalId(),
          sectionTitle: "",
          content: "",
        },
      ],
    }));
  };

  // ----------------------------------------------------
  // Update section by stable ID
  // ----------------------------------------------------

  const updateSection = (
    sectionId: string,
    field: keyof SectionData,
    value: string
  ) => {
    setChapterData((prev) => ({
      ...prev,
      sections: prev.sections.map((section) =>
        section.localId === sectionId
          ? { ...section, [field]: value }
          : section
      ),
    }));
  };

  // ----------------------------------------------------
  // Remove section
  // ----------------------------------------------------

  const removeSection = (sectionId: string) => {
    setChapterData((prev) => ({
      ...prev,
      sections: prev.sections.filter(
        (section) => section.localId !== sectionId
      ),
    }));
  };

  // ----------------------------------------------------
  // Save chapter
  // ----------------------------------------------------

  const handleUpdate = async () => {
    if (
      !librarySlug ||
      !bookSlug ||
      !loadedChapterSlug
    ) {
      toast.error("Missing chapter identifiers");
      return;
    }

    const newSlug = chapterData.chapterSlug.trim();

    if (!newSlug || newSlug.includes("/")) {
      toast.error("Enter a valid chapter slug");
      return;
    }

    if (!chapterData.contentTitle.trim()) {
      toast.error("Content title is required");
      return;
    }

    if (saving) return;

    setSaving(true);

    try {
      const chaptersCollectionPath = [
        "NewBlogs",
        librarySlug,
        "books",
        bookSlug,
        "chapters",
      ] as const;

      const oldRef = doc(
        fireDB,
        ...chaptersCollectionPath,
        loadedChapterSlug
      );

      const newRef = doc(
        fireDB,
        ...chaptersCollectionPath,
        newSlug
      );

      const {
        sections,
        ...chapterFields
      } = chapterData;

      const cleanSections: SectionData[] = sections.map(
        ({ sectionTitle, content }) => ({
          sectionTitle,
          content,
        })
      );

      const payload = {
        ...chapterFields,
        chapterSlug: newSlug,
        sections: cleanSections,
        updatedAt: Timestamp.now(),
      };

      if (newSlug !== loadedChapterSlug) {
        // Prevent overwriting an existing chapter.
        const existing = await getDoc(newRef);

        if (existing.exists()) {
          toast.error(
            "A chapter with this slug already exists"
          );
          return;
        }

        // Create the renamed chapter first.
        await setDoc(newRef, payload);

        // Remove the old chapter document.
        await deleteDoc(oldRef);

        setLoadedChapterSlug(newSlug);
      } else {
        await setDoc(oldRef, payload, { merge: true });
      }

      setChapterData((prev) => ({
        ...prev,
        chapterSlug: newSlug,
      }));

      toast.success("Chapter updated successfully!");
    } catch (error) {
      console.error("Error updating chapter:", error);
      toast.error("Error updating chapter");
    } finally {
      setSaving(false);
    }
  };

  // ----------------------------------------------------
  // Loading
  // ----------------------------------------------------

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="h-10 w-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-700 font-medium">
            Loading chapter data...
          </p>
        </div>
        <ToastContainer position="top-center" />
      </div>
    );
  }

  // ----------------------------------------------------
  // JSX
  // ----------------------------------------------------

  return (
    <div className="bg-gray-100 min-h-screen py-10 px-6">
      <div className="max-w-5xl mx-auto space-y-8">

        {/* Page heading */}
        <motion.h1
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-4xl font-bold text-center mb-6 text-gray-800"
        >
          Update Chapter
        </motion.h1>

        {/* Chapter details */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-gradient-to-r from-red-100 to-blue-100 p-6 rounded-2xl shadow-lg space-y-5"
        >
          <h2 className="text-2xl font-semibold text-gray-800">
            Chapter Details
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block font-semibold mb-2">
                Content Title
              </label>
              <input
                value={chapterData.contentTitle}
                onChange={(e) =>
                  updateField("contentTitle", e.target.value)
                }
                className="w-full border p-3 rounded-lg"
                placeholder="Enter content title"
              />
            </div>

            <div>
              <label className="block font-semibold mb-2">
                Chapter Title
              </label>
              <input
                value={chapterData.title}
                onChange={(e) =>
                  updateField("title", e.target.value)
                }
                className="w-full border p-3 rounded-lg"
                placeholder="Enter chapter title"
              />
            </div>

            <div>
              <label className="block font-semibold mb-2">
                Chapter Slug
              </label>
              <input
                value={chapterData.chapterSlug}
                onChange={(e) =>
                  setChapterData((prev) => ({
                    ...prev,
                    chapterSlug: e.target.value,
                    chapterSlugManual: true,
                  }))
                }
                className="w-full border p-3 rounded-lg"
                placeholder="chapter-slug"
              />
            </div>

            <div>
              <label className="block font-semibold mb-2">
                Author
              </label>
              <input
                value={chapterData.author}
                onChange={(e) =>
                  updateField("author", e.target.value)
                }
                className="w-full border p-3 rounded-lg"
                placeholder="Enter author name"
              />
            </div>
          </div>
        </motion.div>


        {/* Sections */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-yellow-50 p-6 rounded-2xl shadow space-y-6"
        >
          <h2 className="text-3xl font-semibold text-yellow-800">
            Sections
          </h2>

          <AnimatePresence initial={false}>
            {chapterData.sections.map((section, index) => (
              <motion.div
                key={section.localId}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, x: -50 }}
                className="bg-white p-5 rounded-2xl shadow border space-y-4"
              >
                <div className="flex justify-between items-center gap-4">
                  <h3 className="text-lg font-semibold">
                    Section {index + 1}
                  </h3>

                  <button
                    type="button"
                    onClick={() =>
                      removeSection(section.localId)
                    }
                    className="text-red-600 hover:text-red-800 font-medium"
                  >
                    Remove
                  </button>
                </div>

                <div>
                  <label className="block font-medium mb-2">
                    Section Title
                  </label>
                  <input
                    placeholder="Enter section title"
                    value={section.sectionTitle}
                    onChange={(e) =>
                      updateSection(
                        section.localId,
                        "sectionTitle",
                        e.target.value
                      )
                    }
                    className="w-full border p-3 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block font-medium mb-2">
                    Section Content
                  </label>
                  <BlogEditor
                    editorId={section.localId}
                    initialContent={section.content}
                    onChange={(content) =>
                      updateSection(
                        section.localId,
                        "content",
                        content
                      )
                    }
                  />
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          <motion.button
            type="button"
            whileTap={{ scale: 0.95 }}
            whileHover={{ scale: 1.03 }}
            onClick={addSection}
            className="bg-blue-600 text-white px-6 py-3 rounded-xl shadow hover:bg-blue-700 transition-all"
          >
            + Add Section
          </motion.button>
        </motion.div>

        {/* SEO Fields */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-gray-200 p-6 rounded-2xl shadow space-y-4"
        >
          <h2 className="text-2xl font-semibold text-gray-800">
            SEO Settings
          </h2>

          {(
            [
              "seoTitle",
              "seoDescription",
              "seoTags",
              "seoHeading",
              "canonicalUrl",
            ] as const
          ).map((field) => (
            <div key={field} className="space-y-2">
              <label className="block font-medium">
                {field === "canonicalUrl"
                  ? "Canonical URL"
                  : field.replace(/seo/i, "SEO ")}
              </label>

              {field === "seoDescription" ? (
                <textarea
                  placeholder="Enter SEO description"
                  rows={4}
                  className="w-full border p-3 rounded-lg shadow-sm focus:ring-2 focus:ring-gray-400"
                  value={chapterData[field]}
                  onChange={(e) =>
                    updateField(field, e.target.value)
                  }
                />
              ) : (
                <input
                  type="text"
                  placeholder={`Enter ${field}`}
                  className="w-full border p-3 rounded-lg shadow-sm focus:ring-2 focus:ring-gray-400"
                  value={chapterData[field]}
                  onChange={(e) =>
                    updateField(field, e.target.value)
                  }
                />
              )}
            </div>
          ))}
        </motion.div>

        {/* Save button */}
        <motion.button
          type="button"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleUpdate}
          disabled={saving || !loadedChapterSlug}
          className={`w-full text-white p-4 rounded-lg text-xl font-semibold transition-all ${
            saving
              ? "bg-gray-500 cursor-not-allowed"
              : "bg-green-600 hover:bg-green-700"
          }`}
        >
          {saving ? "Updating Chapter..." : "Update Chapter"}
        </motion.button>
      </div>

      <ToastContainer position="top-center" />
    </div>
  );
}
